import {
  resetAccountState,
  resetAnalysesState,
  fetchAnalysesStart,
  fetchAnalysesSuccess,
  resetTransactionState,
  refreshFinish,
  refreshStart,
  resetStatementPageState,
} from '@algoan/dashboard/store/slice';
import { latestAnalysisSelector } from '@algoan/dashboard/store/selectors';
import { AsyncThunk } from '@algoan/dashboard/store/store';
import {
  CashFlow,
  CashFlowConfig,
  Dashboard,
  GroupedCashFlow,
  TransactionCategory,
  AnalysisStatus,
  PaginatedResources,
  Analysis,
  AnalysisErrorCodes,
  NotificationVariant,
  AnalysesErrorMessages,
} from '@algoan/dashboard/model';
import { notify } from './app-state-thunks';
import { fetchAccounts } from './account-thunks';
import { fetchAnalyses, recategorise, fetchEssentials } from './analysis-thunks';
import { fetchOneCustomer } from './customer-thunks';
import { fetchTransactions } from './transaction-thunks';
import { apiUrl } from '@algoan/dashboard/env';
import { authRequest } from './auth-thunks';
import { dashboardMessages } from '@algoan/dashboard/store/messages';
import axios, { CancelTokenSource } from 'axios';
import { batch } from 'react-redux';
import { buildUrl } from '@algoan/dashboard/utils';

export let analysisPageCancellationToken: CancelTokenSource | undefined;

const delay = (milliseconds: number): Promise<unknown> => new Promise((response) => setTimeout(response, milliseconds));

export const fetchNewAggregationData =
  (customerId: string, newAnalysisId: string): AsyncThunk =>
  async (dispatch): Promise<void> => {
    analysisPageCancellationToken = axios.CancelToken.source();
    let analysisId: string | undefined = '';
    let requestNumber = 0;
    let resources: Analysis[] | undefined = undefined;
    const analysesUrl = buildUrl(`${apiUrl}/v2/customers/${customerId}/analyses`, [
      {
        key: 'limit',
        value: 1,
      },
      {
        key: 'filter',
        value: {
          status: { $regex: `${AnalysisStatus.COMPLETED}|${AnalysisStatus.ERROR}` },
        },
      },
      {
        key: 'sort',
        value: { createdAt: -1 },
      },
    ]);

    do {
      if (requestNumber > 0) {
        await delay(2000);
      }
      const { data } = await dispatch(
        authRequest<PaginatedResources<Analysis>>('GET', analysesUrl, undefined, {
          cancelToken: analysisPageCancellationToken?.token,
        }),
      );

      analysisId = data.resources.length > 0 ? data.resources[0].id : undefined;
      resources = data.resources;

      if (!analysisId) {
        // do nothing because error is already handled in analysis action
        throw AnalysesErrorMessages.TECHNICAL_ERROR;
      }

      requestNumber++;
    } while (newAnalysisId !== analysisId && requestNumber < 10);

    if (requestNumber >= 10) {
      throw AnalysesErrorMessages.TECHNICAL_ERROR;
    }

    if (resources[0]?.error?.code === AnalysisErrorCodes.NO_CHECKING_ACCOUNT) {
      throw AnalysesErrorMessages.NO_CHECKING_ACCOUNT;
    }
    if (resources.length === 0 || resources[0]?.status === 'ERROR') {
      throw AnalysesErrorMessages.TECHNICAL_ERROR;
    }

    dispatch(resetAggregationData());

    batch(() => {
      dispatch(resetAnalysesState());
      dispatch(fetchAnalysesStart());
    });

    dispatch(fetchAnalysesSuccess({ analyses: resources }));
    dispatch(fetchAccounts({ customerId, analysisId }));
    dispatch(fetchTransactions(customerId, analysisId));
    dispatch(fetchEssentials(customerId, analysisId));
  };

export const fetchAggregationData =
  (customerId: string): AsyncThunk =>
  async (dispatch, getState): Promise<void> => {
    analysisPageCancellationToken = axios.CancelToken.source();

    dispatch(resetAggregationData());

    await dispatch(fetchAnalyses(customerId));
    const state = getState();
    const latestAnalysis = latestAnalysisSelector(state);
    if (!latestAnalysis) {
      // do nothing because error is already handled in analysis action
      return;
    }

    const analysisId = latestAnalysis.id;

    dispatch(fetchAccounts({ customerId, analysisId }));
    dispatch(fetchTransactions(customerId, analysisId));
    dispatch(fetchEssentials(customerId, analysisId));
  };

export const resetAggregationData =
  (): AsyncThunk =>
  async (dispatch): Promise<void> => {
    batch(() => {
      dispatch(resetAnalysesState());
      dispatch(resetTransactionState());
      dispatch(resetAccountState());
      dispatch(resetStatementPageState());
    });
  };

export const modifyCategoryAndUpdateAggregationData =
  (
    customerId: string,
    analysisId: string,
    cashFlow: GroupedCashFlow | CashFlow,
    newCategory: TransactionCategory,
  ): AsyncThunk<void> =>
  async (dispatch) => {
    try {
      dispatch(refreshStart());
      await dispatch(recategorise(customerId, analysisId, cashFlow, newCategory));
      await dispatch(fetchOneCustomer(customerId));
      await dispatch(fetchAggregationData(customerId));
      dispatch(
        notify({
          type: NotificationVariant.SUCCESS,
          message: dashboardMessages.recategorisationSuccess,
        }),
      );
    } catch (error) {
      dispatch(
        notify({
          type: NotificationVariant.ERROR,
          message: dashboardMessages.recategorisationError,
        }),
      );
    } finally {
      dispatch(refreshFinish());
    }
  };

export const saveSelectedCashFlows =
  (
    customerId: string,
    analysisId: string,
    cashFlowIds: string[],
    isIncludedInRemainderToLive: boolean,
  ): AsyncThunk<void> =>
  async (dispatch) => {
    const url = `${apiUrl}/v2/customers/${customerId}/dashboard`;
    const body: Partial<Dashboard> = {
      analysesConfig: {
        [analysisId]: cashFlowIds.reduce<Record<string, CashFlowConfig>>((accumulator, cashFlowId) => {
          accumulator[cashFlowId] = { isIncludedInRemainderToLive };

          return accumulator;
        }, {}),
      },
    };

    try {
      await dispatch(authRequest('patch', url, body));
    } catch (error) {
      throw error;
    }
  };

export const saveNewMonthlyAmount =
  (customerId: string, newMonthlyAmount: number): AsyncThunk<void> =>
  async (dispatch) => {
    const url = `${apiUrl}/v2/customers/${customerId}/dashboard`;
    try {
      await dispatch(authRequest('patch', url, { customizedMonthlyAmount: newMonthlyAmount }));
    } catch (error) {
      throw error;
    }
  };

export const uploadPDFBankStatements =
  (customerIdentifier: string, pdfFiles: string[]): AsyncThunk<void> =>
  async () => {
    const url = 'https://europe-west1-algoan-prod.cloudfunctions.net/uploadPdf';

    const body = {
      base64Files: pdfFiles,
      customIdentifier: customerIdentifier,
    };

    await axios.post(url, body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };
